import type {
  Celebrity,
  Media,
  Recommendation,
  LeaderboardItem,
  PaginatedResponse,
} from '@/types'
import { transformCelebrity, transformMedia, transformLeaderboardItem, transformRecommendation } from '@/lib/transform'

const API_URL = import.meta.env.VITE_API_URL || 'https://celebrity-picks-api.fantastic-jarvis.workers.dev'

const noCacheFetchOptions: RequestInit =
  new URLSearchParams(window.location.search).get('nocache') === '1'
    ? { cache: 'no-store' }
    : {}

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, noCacheFetchOptions)

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }

  return response.json() as Promise<T>
}

// Celebrities
export async function getCelebrities(page = 1, perPage = 20, category?: string): Promise<PaginatedResponse<Celebrity>> {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
  if (category) params.append('category', category)
  const data = await fetchApi<any>(`/api/v1/celebrities?${params}`)
  return {
    data: (data.data || []).map(transformCelebrity),
    pagination: {
      page: data.pagination?.page || 1,
      perPage: data.pagination?.per_page || 20,
      total: data.pagination?.total || 0,
      totalPages: data.pagination?.total_pages || 1,
    },
  }
}

export async function getCelebrity(slug: string): Promise<Celebrity> {
  const data = await fetchApi<any>(`/api/v1/celebrities/${slug}`)
  return transformCelebrity(data)
}

export async function getCelebrityRecommendations(slug: string): Promise<Recommendation[]> {
  const data = await fetchApi<any[]>(`/api/v1/celebrities/${slug}/recommendations`)
  return data.map(transformRecommendation)
}

// Media
export async function getMedia(page = 1, perPage = 20, filters?: Record<string, string>): Promise<PaginatedResponse<Media>> {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value)
    })
  }
  const data = await fetchApi<any>(`/api/v1/media?${params}`)
  return {
    data: (data.data || []).map(transformMedia),
    pagination: {
      page: data.pagination?.page || 1,
      perPage: data.pagination?.per_page || 20,
      total: data.pagination?.total || 0,
      totalPages: data.pagination?.total_pages || 1,
    },
  }
}

export async function getMediaItem(slug: string): Promise<Media> {
  const data = await fetchApi<any>(`/api/v1/media/${slug}`)
  return transformMedia(data)
}

export async function getMediaRecommendations(slug: string): Promise<Recommendation[]> {
  const data = await fetchApi<any[]>(`/api/v1/media/${slug}/recommendations`)
  return data.map(transformRecommendation)
}

// Leaderboard
export async function getLeaderboard(limit = 50, type?: string): Promise<LeaderboardItem[]> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (type) params.append('type', type)
  const data = await fetchApi<any[]>(`/api/v1/leaderboard?${params}`)
  return data.map(transformLeaderboardItem)
}

// Search
export async function search(query: string): Promise<{ celebrities: Celebrity[]; media: Media[] }> {
  const data = await fetchApi<any>(`/api/v1/search?q=${encodeURIComponent(query)}`)
  return {
    celebrities: (data.celebrities || []).map(transformCelebrity),
    media: (data.media || []).map(transformMedia),
  }
}

// Newsletter
export function subscribeToNewsletter(email: string) {
  return fetch(`${API_URL}/api/v1/newsletter/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
}

// Stats
export async function getStats(): Promise<{ celebrities: number; media: number; recommendations: number }> {
  return fetchApi('/api/v1/stats')
}

// Recently Added
export async function getRecentlyAdded(): Promise<{ celebrities: any[]; media: any[] }> {
  return fetchApi('/api/v1/recently-added')
}
